/**
 * renderBlock.ts — **the ONLY place markup is made.** design.md §5.4.
 *
 * AGENTS.md: "Books has a monument to this: three independent copies of one page grid, and when
 * two were removed the third kept its *names* and lost its *geometry* — four of six layouts
 * silently collapsed into one. Sharing the names is not sharing the layout. One renderer, and a
 * test that the exporter agrees with it."
 *
 * If you are about to write `<span data-s=` anywhere else in this repository — an EPUB exporter, a
 * share page, a print stylesheet, a search-result snippet — import this file instead. The
 * highlight, the cue list and the column map all address `[data-s]` elements that *already exist*;
 * a second renderer that emits one fewer span is the fragment-id divergence this repo was written
 * to prevent, and it produces no error anywhere.
 *
 * ── Why character ranges and not a tree ──────────────────────────────────────
 * §2.2: styling is `Run{start,end,style}` over the block's one canonical `text`, so "the
 * intersection of a bold run and a spoken sentence" is arithmetic on two integer ranges rather
 * than a tree walk. A run that straddles a sentence boundary is emitted as **two elements** —
 * correct HTML, invisible CSS — because the sentence span is the thing that must be whole.
 *
 * ── Not JSX ─────────────────────────────────────────────────────────────────
 * The file is `.ts` (docs/design.md §11 names it that), so this uses `createElement` directly.
 * That is a happy accident: the boundary walk reads better as data than as markup, and it keeps
 * the two halves — `sliceBlock` (pure, no React, unit-testable in node) and the element factory —
 * visibly separate.
 */

import { createElement, type ReactElement, type ReactNode } from "react";
import type { Block, Run, RunStyle, Span } from "../../../shared/doc.ts";

/**
 * One maximal run of characters over which nothing changes: the same sentence (or none) and the
 * same set of style runs. Pure data — no React, no DOM.
 */
export interface Piece {
  /** Half-open offsets into `block.text`. */
  start: number;
  end: number;
  text: string;
  /** The sid whose span contains this piece, or null for text between sentences. */
  sid: string | null;
  /** Every run covering this piece, in the block's declared order. */
  runs: readonly Run[];
}

/**
 * Take the union of every boundary offset (0, each run start/end, each sentence start/end,
 * length), sort, dedupe, and emit the maximal pieces between consecutive cuts.
 *
 * Because we cut at every run boundary, a run either **fully covers** a piece or does not touch
 * it — which is what makes the per-piece style set a filter rather than an interval intersection.
 *
 * Malformed ranges (an ingest bug, or a re-import that shifted offsets) are clamped into the
 * block and dropped if they collapse, rather than throwing: a book with one bad `<em>` must still
 * be readable. The symptom of a dropped run is unstyled text, which is visible; the symptom of a
 * throw here is an empty chapter, which looks like the whole product is broken.
 */
export function sliceBlock(block: Block): Piece[] {
  const text = block.text;
  const len = text.length;
  if (len === 0) return [];

  const runs = normalizeRanges(block.runs, len);
  const sentences = block.sentences;

  const cuts = new Set<number>([0, len]);
  for (const r of runs) {
    cuts.add(r.start);
    cuts.add(r.end);
  }
  for (const s of sentences) {
    if (s.start >= 0 && s.start <= len) cuts.add(s.start);
    if (s.end >= 0 && s.end <= len) cuts.add(s.end);
  }

  const edges = [...cuts].sort((a, b) => a - b);
  const pieces: Piece[] = [];

  for (let i = 0; i < edges.length - 1; i++) {
    const a = edges[i]!;
    const b = edges[i + 1]!;
    if (b <= a) continue;
    pieces.push({
      start: a,
      end: b,
      text: text.slice(a, b),
      sid: sentenceAt(sentences, a, b),
      runs: runs.filter((r) => r.start <= a && r.end >= b),
    });
  }

  return pieces;
}

function normalizeRanges(runs: readonly Run[], len: number): Run[] {
  const out: Run[] = [];
  for (const r of runs) {
    const start = Math.max(0, Math.min(len, Math.trunc(r.start)));
    const end = Math.max(0, Math.min(len, Math.trunc(r.end)));
    if (!(end > start)) continue;
    out.push(start === r.start && end === r.end ? r : { ...r, start, end });
  }
  return out;
}

/** Sentence spans are half-open and non-overlapping (shared/text/segment.ts guarantees it). */
function sentenceAt(sentences: readonly Span[], a: number, b: number): string | null {
  for (const s of sentences) {
    if (s.start <= a && s.end >= b) return s.id;
  }
  return null;
}

/**
 * Nesting order, outermost first. Fixed, because two renderers that nest `<em>` and `<strong>`
 * differently produce different DOM for the same book — and the reader's `[data-s]` spans are
 * addressed by CSS selectors that a reviewer reads once and assumes forever.
 *
 * `noteref` is outermost so a footnote marker that is also a link renders `<sup><a>…</a></sup>`,
 * which is how every book sets one and what screen readers announce correctly.
 */
const STYLE_ORDER: readonly RunStyle[] = ["noteref", "link", "strong", "em", "smallcaps", "sup", "sub", "code"];

/**
 * Schemes that hand off to the browser or another app and therefore cannot destroy the reader.
 * Everything else — a relative path, a bare `#fragment`, `javascript:`, `data:` — is in-book or
 * hostile and is rendered inert. See the `link` case in `wrap()`.
 */
const LEAVES_THE_PAGE = /^(?:https?|mailto|tel):/i;

export interface RenderBlockOptions {
  /**
   * Resolve a stored image sha to a URL. Supplied by the reader because the route is
   * `/api/v1/books/:bookId/images/:sha` and a Block does not know its book — deliberately: a Block
   * is edition-scoped data, and baking a bookId into it would make the same block un-renderable
   * from the share page (§10) where the route differs.
   */
  imageSrc?: (sha: string) => string;
  /**
   * True for the chapter's own title block. Only used to add `.ab-chapter-title`, which is what
   * the drop cap in ui/reader.css hangs off. Every other heading is a section heading.
   */
  chapterTitle?: boolean;
  /**
   * This block is part of a hard-wrapped run — one sentence the source broke across several `<p>`s.
   * Adds `data-flow`, which `reader.css` turns into `display: inline` so the run reads as the single
   * paragraph it is. See `shared/text/flow.ts` for the rule and why it fires on one book.
   *
   * ⚠️ Presentation only. The element, its `data-b`, and every `[data-s]` span inside it are
   * unchanged — which is what keeps the column map (`Paginator` queries `[data-s]` and uses
   * `getClientRects()`, never block geometry), the cue list and every stored sid exactly as they
   * were. Nothing is re-segmented and no clip is re-keyed.
   */
  flow?: boolean;
}

/**
 * One `Block` → one element. Never a Fragment: the column map, the follow rule
 * (`[data-b]:has(.narrating)`) and `chapters.parts` all assume a block is one box.
 */
export function renderBlock(block: Block, opts: RenderBlockOptions = {}): ReactElement {
  const props: Record<string, unknown> = {
    key: block.id,
    "data-b": block.id,
    "data-kind": block.kind,
    // §5.4 rule 2, and the highest-value one-attribute detail in the reader: **Safari silently
    // refuses to hyphenate an element with no resolvable language**, and a 34-character phone
    // column with no hyphenation is unreadable. `lang` is REQUIRED on Block for this reason.
    lang: block.lang,
  };
  // §5.4 rule 3: `speak:false` blocks render normally with `data-mute`, so the highlighter can
  // never select them and the sentence menu (P3) shows different actions. There is deliberately
  // no visual difference — a running head ingest kept but will not narrate is still part of the
  // book on the page.
  if (!block.speak) props["data-mute"] = "1";
  if (opts.flow) props["data-flow"] = "1";

  if (block.kind === "image") return renderImage(block, props, opts);
  if (block.kind === "rule") return renderRule(block, props);

  const children = renderInline(block);

  switch (block.kind) {
    case "heading": {
      const level = clampLevel(block.level);
      if (opts.chapterTitle) props["className"] = "ab-chapter-title";
      return createElement(`h${level}`, props, children);
    }
    case "verse":
      props["className"] = "ab-verse-line";
      return createElement("p", props, children);
    case "quote":
      return createElement("blockquote", props, children);
    case "list-item":
      // A flat `<li>` with no `<ul>` parent is a lie to the accessibility tree, and the document
      // model has no list container to build one from (§2.2 stores blocks flat). A paragraph with
      // a marker pseudo-element is honestly what we have.
      props["className"] = "ab-li";
      return createElement("p", props, children);
    case "caption":
      return createElement("figcaption", props, children);
    case "code":
      return createElement("pre", props, createElement("code", null, children));
    case "table":
      // §2.2: "unknown content is preserved, never flattened — kind: table | code is rendered,
      // addressable, searchable, and never narrated." Ingest hands us collapsed plain text, not a
      // grid; rendering a `<table>` we do not have would be inventing structure that is not there.
      props["className"] = "ab-table";
      return createElement("div", props, children);
    case "note":
      props["className"] = "ab-note";
      return createElement("aside", props, children);
    case "para":
    default:
      return createElement("p", props, children);
  }
}

function clampLevel(level: number | undefined): number {
  if (typeof level !== "number" || !Number.isFinite(level)) return 2;
  return Math.max(1, Math.min(6, Math.trunc(level)));
}

function renderRule(block: Block, props: Record<string, unknown>): ReactElement {
  // A stanza break or a scene break. It has no text and no sentences, so it is never narrated and
  // never highlighted — it is pure vertical rhythm. §2.2: it is a *real block*, not a `""` hole in
  // a parallel array, precisely so nothing can `filter(Boolean)` it away and shift every later
  // highlight by one, silently, forever.
  props["className"] = "ab-rule";
  props["role"] = "separator";
  if (block.text.length === 0) props["data-plain"] = "1";
  return createElement("div", props, block.text.length ? renderInline(block) : null);
}

function renderImage(block: Block, props: Record<string, unknown>, opts: RenderBlockOptions): ReactElement {
  const img = block.image;
  if (!img) {
    // An image block with no image is an ingest bug, not a render-time condition to paper over.
    // Emitting an empty figure keeps the block addressable (a sid may point into its alt text) and
    // keeps the page count stable, which is more useful than throwing inside a render.
    return createElement("figure", props);
  }
  const src = opts.imageSrc?.(img.sha);
  const children: ReactNode[] = [];
  if (src) {
    children.push(
      createElement("img", {
        key: "img",
        src,
        alt: img.alt,
        // Intrinsic dimensions are not decoration: without them the image has zero height until it
        // loads, the multicol track measures short, `scrollWidth` yields the wrong page count, and
        // the reader is repaginated *after* landing — which moves them mid-read. Reader.tsx also
        // waits for images to settle before the first measure; these attributes are what make the
        // measure right even when that wait times out.
        width: img.w > 0 ? img.w : undefined,
        height: img.h > 0 ? img.h : undefined,
        decoding: "async",
        loading: "eager", // NOT lazy: a lazily-loaded image inside a column that is off-screen
        // never loads (it is clipped, not scrolled to), and the page stays blank forever.
      }),
    );
  } else if (img.alt) {
    children.push(createElement("figcaption", { key: "alt" }, img.alt));
  }
  return createElement("figure", props, children);
}

/**
 * The sentence spans, and the run elements inside them.
 *
 * §5.4 rule 1: **inter-sentence whitespace is emitted outside any `[data-s]` span.** Inside it,
 * the highlight wash extends past the period, and at a line end `box-decoration-break: clone`
 * paints a trailing bar into the margin. It falls out for free here because the segmenter's spans
 * exclude surrounding whitespace, so those pieces carry `sid: null` and are emitted bare.
 */
function renderInline(block: Block): ReactNode[] {
  const pieces = sliceBlock(block);
  const out: ReactNode[] = [];

  let i = 0;
  while (i < pieces.length) {
    const sid = pieces[i]!.sid;
    if (sid === null) {
      out.push(renderPiece(pieces[i]!, `p${i}`));
      i++;
      continue;
    }
    // Every piece of one sentence goes inside ONE span. Sentence-id → DOM is
    // `track.querySelector('[data-s="…"]')` and nothing else (§5.4), so a sentence must be exactly
    // one element even when a bold run splits it into three pieces.
    const kids: ReactNode[] = [];
    const from = i;
    while (i < pieces.length && pieces[i]!.sid === sid) {
      kids.push(renderPiece(pieces[i]!, `p${i}`));
      i++;
    }
    out.push(createElement("span", { key: `s${from}`, "data-s": sid }, kids));
  }

  return out;
}

function renderPiece(piece: Piece, key: string): ReactNode {
  if (piece.runs.length === 0) return piece.text;

  // Deterministic nesting. Each style contributes at most one element per piece; a block that
  // declares two `em` runs over the same characters gets one `<em>`, which is what it meant.
  const applied: Run[] = [];
  for (const style of STYLE_ORDER) {
    const run = piece.runs.find((r) => r.style === style);
    if (run) applied.push(run);
  }
  if (applied.length === 0) return piece.text;

  let node: ReactNode = piece.text;
  for (let i = applied.length - 1; i >= 0; i--) {
    node = wrap(applied[i]!, node, i === applied.length - 1 ? key : `${key}-${i}`);
  }
  return node;
}

function wrap(run: Run, child: ReactNode, key: string): ReactElement {
  switch (run.style) {
    case "em":
      return createElement("em", { key }, child);
    case "strong":
      return createElement("strong", { key }, child);
    case "code":
      return createElement("code", { key }, child);
    case "smallcaps":
      return createElement("span", { key, className: "ab-sc" }, child);
    case "sup":
      return createElement("sup", { key }, child);
    case "sub":
      return createElement("sub", { key }, child);
    case "noteref":
      return createElement("sup", { key, className: "ab-noteref" }, child);
    case "link": {
      const href = run.href;
      if (!href) return createElement("span", { key }, child);
      // An external link opens away from the book. `noreferrer` matters here beyond the usual:
      // `Referrer-Policy: same-origin` is set on every response and share tokens are scrubbed from
      // the log (§6.4), precisely so an unlisted book's
      // URL never leaves the device, and a bare `target="_blank"` in the reader would leak it from
      // inside the page. `mailto:` and `tel:` are in the list because they hand off to another app
      // and never navigate the document.
      if (LEAVES_THE_PAGE.test(href)) {
        return createElement("a", { key, href, target: "_blank", rel: "noreferrer noopener" }, child);
      }
      /**
       * ⚠️ **An in-book href must NEVER reach the `href` attribute.** Gutenberg and Calibre EPUBs —
       * the two fixtures this repo is built around — are full of cross-references and in-chapter TOC
       * anchors, and they are relative: `0002.xhtml#c3`, `#pgepubid00012`.
       *
       * Rendered as a real link, a tap did a real navigation to `/b/bk_x/0002.xhtml`. That is not a
       * route, so `@fastify/static`'s SPA fallback served `index.html`, `routes.tsx` bounced it to
       * `<Navigate to="/">`, and **the book vanished to the shelf mid-read**, losing the mounted
       * chapter and the pagination. One tap on any cross-reference, in any Gutenberg book.
       *
       * P1 cannot resolve these honestly: the fragment→sid map lives in `RawBlock.anchors`, which the
       * worker computes for §3.4 signal 5 and does not persist. So the target is kept verbatim in
       * `data-href` (that is what a resolver will read when one exists) and the element is emitted
       * **without `href`**, which is exactly what makes it inert — no `preventDefault`, no handler, no
       * way for a future refactor to reintroduce the navigation. It is styled as a cross-reference by
       * `.ab-xref` in ui/reader.css so it does not silently become plain text either.
       */
      return createElement("a", { key, className: "ab-xref", "data-href": href }, child);
    }
    default:
      return createElement("span", { key }, child);
  }
}
